<template>
  <div class="demo-wrapper">
    <!-- Demo banner -->
    <div class="demo-banner">
      <span class="demo-banner-text">
        🔒 Demo mode — {{ formatTime(timeLeft) }} remaining
      </span>
      <span class="demo-banner-cta" @click="goToPricing">Upgrade to Pro</span>
    </div>
    <!-- Render the real Chart component -->
    <Chart ref="chart" />
  </div>
</template>

<script>
import Chart from '../Chart.vue'

export default {
  name: 'DemoChart',
  components: { Chart },
  data() {
    return {
      timeLeft: 180, // 3 minutes in seconds
      timer: null,
      storageKey: 'demo-chart-usage',
      sessionStartTime: null
    }
  },
  mounted() {
    this.loadSessionData();
    this.startTimer();
  },
  beforeDestroy() {
    this.clearTimer();
    this.saveSessionData();
  },
  methods: {
    loadSessionData() {
      try {
        const data = localStorage.getItem(this.storageKey);
        if (data) {
          const parsed = JSON.parse(data);
          const now = Date.now();
          
          // Check if the last session was within a reasonable time frame (e.g., 1 hour)
          if (parsed.lastSessionTime && (now - parsed.lastSessionTime) < 3600000) {
            this.sessionStartTime = parsed.sessionStartTime;
            
            // Calculate remaining time based on elapsed time
            if (this.sessionStartTime) {
              const elapsed = Math.floor((now - this.sessionStartTime) / 1000);
              const usedTime = parsed.usedTime || 0;
              const totalUsed = usedTime + elapsed;
              
              // Check if we've exceeded the 3-minute demo time
              if (totalUsed >= 180) {
                this.endDemo();
                return;
              }
              
              // Calculate remaining time
              this.timeLeft = Math.max(0, 180 - totalUsed);
            }
          } else {
            // Reset for new session if too much time has passed
            this.resetSession();
          }
        }
      } catch (error) {
        console.error('Error loading session data:', error);
        this.resetSession();
      }
    },
    
    saveSessionData() {
      try {
        const now = Date.now();
        const usedTime = this.sessionStartTime ? 
          Math.floor((now - this.sessionStartTime) / 1000) : 0;
        
        const data = {
          sessionStartTime: this.sessionStartTime,
          lastSessionTime: now,
          usedTime: usedTime
        };
        
        localStorage.setItem(this.storageKey, JSON.stringify(data));
      } catch (error) {
        console.error('Error saving session data:', error);
      }
    },
    
    resetSession() {
      this.timeLeft = 180;
      this.sessionStartTime = Date.now();
      localStorage.removeItem(this.storageKey);
    },
    
    startTimer() {
      if (!this.sessionStartTime) {
        this.sessionStartTime = Date.now();
      }
      
      this.timer = setInterval(() => {
        this.timeLeft--;
        
        // Auto-save every 10 seconds
        if (this.timeLeft % 10 === 0) {
          this.saveSessionData();
        }
        
        if (this.timeLeft <= 0) {
          this.endDemo();
        }
      }, 1000);
    },
    
    clearTimer() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    },
    
    endDemo() {
      this.clearTimer();
      this.saveSessionData();
      this.goToPricing();
    },
    goToPricing() {
      this.clearTimer();
      // Clear session data when going to pricing
      localStorage.removeItem(this.storageKey);
      if (this.$clerk && this.$clerk.isAuthenticated) {
        this.$router.replace('/pricing')
      } else {
        this.$router.replace('/')
        this.$nextTick(() => {
          if (this.$clerk) this.$clerk.openSignUp()
        })
      }
    },
    
    formatTime(seconds) {
      const mins = Math.floor(seconds / 60);
      const secs = seconds % 60;
      return `${mins}:${secs.toString().padStart(2, '0')}`;
    }
  }
}
</script>

<style scoped>
.demo-wrapper {
  position: relative;
  width: 100%;
  height: 100vh;
}
.demo-banner {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  height: 36px;
  background: linear-gradient(90deg, #dc2626, #b91c1c);
  color: #fff;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 13px;
  font-weight: 500;
}
.demo-banner-text {
  letter-spacing: 0.3px;
}
.demo-banner-cta {
  padding: 3px 14px;
  background: #fff;
  color: #dc2626;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s;
}
.demo-banner-cta:hover {
  background: #fef2f2;
}
</style>
